// Leads panel — WhatConverts call outcomes as a donut. Self-contained frame:
// no shared-file edits, ports the TrendChart.tsx recharts/theme conventions
// (ResponsiveContainer, CSS-var tick/tooltip styling) plus the donut+legend
// shape already established by components/usage/UsageModelsDonut.tsx.

import { Cell, Pie, PieChart, ResponsiveContainer, Tooltip } from "recharts";

export interface CallOutcomesDonutProps {
	answered: number;
	missed: number;
	voicemail: number;
	unclassified?: number;
}

// Demo sample for storybook-less previews / empty-state development.
export const demoCallOutcomes: CallOutcomesDonutProps = {
	answered: 142,
	missed: 38,
	voicemail: 21,
	unclassified: 12,
};

// Fixed Core-palette hexes (not theme CSS vars): call-outcome color identity
// must read the same in light and dark mode, unlike `--color-coral`/`--color-teal`
// which intentionally swap value across the two themes.
const OUTCOME_COLORS = {
	answered: "#0A76A8", // azure — primary
	voicemail: "#4CC9F0", // accent blue
	missed: "#6B7280", // grey
	unclassified: "#9CA3AF", // light grey
} as const;

interface Slice {
	key: keyof typeof OUTCOME_COLORS;
	name: string;
	value: number;
	color: string;
}

export function CallOutcomesDonut({
	answered,
	missed,
	voicemail,
	unclassified = 0,
}: CallOutcomesDonutProps) {
	const allSlices: Slice[] = [
		{ key: "answered", name: "Answered", value: answered, color: OUTCOME_COLORS.answered },
		{ key: "voicemail", name: "Voicemail", value: voicemail, color: OUTCOME_COLORS.voicemail },
		{ key: "missed", name: "Missed", value: missed, color: OUTCOME_COLORS.missed },
		{
			key: "unclassified",
			name: "Not classified",
			value: unclassified,
			color: OUTCOME_COLORS.unclassified,
		},
	];
	const total = allSlices.reduce((sum, s) => sum + s.value, 0);
	const slices = allSlices.filter((s) => s.value > 0);

	if (total <= 0) {
		return (
			<div
				className="flex items-center justify-center text-dash-context text-muted-foreground"
				style={{ height: 180 }}
			>
				No call data yet
			</div>
		);
	}

	return (
		<div className="flex items-center gap-4" style={{ height: 180 }}>
			<div className="relative h-full w-[150px] shrink-0">
				<ResponsiveContainer width="100%" height="100%">
					<PieChart>
						<Pie
							data={slices}
							dataKey="value"
							nameKey="name"
							cx="50%"
							cy="50%"
							innerRadius={48}
							outerRadius={72}
							paddingAngle={2}
							stroke="#FFFFFF"
							strokeWidth={2}
							isAnimationActive={false}
						>
							{slices.map((s) => (
								<Cell key={s.key} fill={s.color} />
							))}
						</Pie>
						<Tooltip
							contentStyle={{
								borderRadius: 8,
								border: "1px solid var(--color-border)",
								fontSize: 12,
							}}
							formatter={(value: number, name) => [
								`${value.toLocaleString()} call${value === 1 ? "" : "s"} (${Math.round(
									(value / total) * 100,
								)}%)`,
								name,
							]}
						/>
					</PieChart>
				</ResponsiveContainer>
				{/* Center total — pointer-events-none so the pie's hover/tooltip
				    layer underneath stays interactive. */}
				<div className="pointer-events-none absolute inset-0 flex flex-col items-center justify-center">
					<span className="tabular text-dash-value font-semibold text-foreground">
						{total.toLocaleString()}
					</span>
					<span className="text-dash-context text-muted-foreground">calls</span>
				</div>
			</div>
			<ul className="min-w-0 flex-1 space-y-1.5">
				{allSlices.map((s) => (
					<li key={s.key} className="flex items-center gap-2">
						<span
							className="h-2.5 w-2.5 shrink-0 rounded-full"
							style={{ background: s.color }}
						/>
						{/* No `truncate` here on purpose -- `truncate` sets
						    `overflow: hidden`, and per the CSS flexbox spec that
						    disables the browser's automatic min-content protection
						    for this flex item, letting the neighboring shrink-0
						    count column squeeze it down to just 2-3 characters
						    before an ellipsis ("Answered" -> "An...", "Not
						    classified" -> "Not classi...", confirmed live on every
						    client). Leaving width/overflow unset re-enables that
						    protection (the label won't shrink below its widest
						    unbreakable word) and lets it wrap onto a second line
						    instead when the card is genuinely narrow, so the full
						    label is always visible without needing the `title`
						    tooltip as the only way to read it. */}
						<span className="text-dash-context text-foreground" title={s.name}>
							{s.name}
						</span>
						<span className="tabular ml-auto shrink-0 text-dash-context text-muted-foreground">
							{s.value.toLocaleString()} · {total > 0 ? Math.round((s.value / total) * 100) : 0}%
						</span>
					</li>
				))}
			</ul>
		</div>
	);
}
